
import React, { useEffect, useState } from 'react';
import { StaffAssignmentService, type ClassStaffAssignment as ClassStaffAssignmentType } from '@/services/classServices/staffAssignmentService';
import { getStaffMembers } from '@/services/staff/staffQueryService';
import { StaffMember } from '@/types/staffTypes';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

interface ClassStaffAssignmentProps {
  classId: string;
  organizationId: string;
}

const ClassStaffAssignment: React.FC<ClassStaffAssignmentProps> = ({ classId, organizationId }) => {
  const [assignedStaff, setAssignedStaff] = useState<ClassStaffAssignmentType[]>([]);
  const [allStaff, setAllStaff] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<string>('');

  // Load data
  const fetchAssignments = async () => {
    setLoading(true);
    const assignments = await StaffAssignmentService.getAssignmentsByClass(classId);
    setAssignedStaff(assignments);
    setLoading(false);
  };

  useEffect(() => {
    fetchAssignments();
    getStaffMembers().then(setAllStaff);
    // eslint-disable-next-line
  }, [classId]);

  // Assign
  const handleAssign = async () => {
    if (!selectedStaff) return;
    setAssigning(true);
    try {
      await StaffAssignmentService.assignStaffToClass(classId, selectedStaff, organizationId);
      toast.success('Staff assigned to class');
      setSelectedStaff('');
      fetchAssignments();
    } catch (e: any) {
      toast.error(e?.message || 'Error assigning staff');
    } finally {
      setAssigning(false);
    }
  };

  // Unassign
  const handleUnassign = async (staffId: string) => {
    setAssigning(true);
    try {
      await StaffAssignmentService.unassignStaffFromClass(classId, staffId);
      toast.success('Staff unassigned from class');
      fetchAssignments();
    } catch (e: any) {
      toast.error(e?.message || 'Error unassigning staff');
    } finally {
      setAssigning(false);
    }
  };

  // Filter out already assigned staff from add list
  const availableStaff = allStaff.filter(
    s => !assignedStaff.some(a => a.staff_id === s.id)
  );

  return (
    <div className="mb-4">
      <h4 className="font-semibold text-hivebubble-slate mb-2">Assigned Instructors/Staff</h4>
      {loading ? (
        <div className="text-sm text-gray-500">Loading...</div>
      ) : assignedStaff.length === 0 ? (
        <div className="text-sm text-gray-400">No staff assigned</div>
      ) : (
        <ul className="space-y-2 mb-2">
          {assignedStaff.map(a => (
            <li key={a.staff_id} className="flex items-center gap-2">
              <div className="font-medium">
                {a.staff_member?.first_name} {a.staff_member?.last_name}
                {a.staff_member?.role && (
                  <span className="ml-1 text-xs text-muted-foreground">({a.staff_member.role})</span>
                )}
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={assigning}
                onClick={() => handleUnassign(a.staff_id)}
                className="ml-2 text-xs"
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-center gap-2 mt-2">
        <select
          className="border rounded px-2 py-1"
          value={selectedStaff}
          onChange={e => setSelectedStaff(e.target.value)}
          disabled={assigning}
        >
          <option value="">Assign staff...</option>
          {availableStaff.map(s => (
            <option key={s.id} value={s.id}>
              {s.first_name} {s.last_name} {s.role ? `(${s.role})` : ''}
            </option>
          ))}
        </select>
        <Button 
          size="sm"
          onClick={handleAssign}
          disabled={!selectedStaff || assigning}
        >
          Assign
        </Button>
      </div>
    </div>
  );
};

export default ClassStaffAssignment;
